import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  FormControl,
  FormLabel,
  Progress,
  Badge,
  useToast,
  Icon,
  Divider,
  List,
  ListItem,
  ListIcon,
  useColorModeValue
} from '@chakra-ui/react';
import Card from "../../../../../components/Card/Card.js";
import CardBody from "../../../../../components/Card/CardBody.js";
import CardHeader from "../../../../../components/Card/CardHeader.js";
import { FiUpload, FiFile, FiCheck, FiX, FiUser, FiCreditCard } from 'react-icons/fi';
import paymentSyncService from '../../../../../services/paymentSync';


const NapsisImporter = ({ onDataImported, onError }) => {
  const [file, setFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [parseResult, setParsResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  const fileInputRef = useRef(null);
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    
    // Validar tipo de archivo
    const validTypes = ['.html', '.htm', '.xls', '.xlsx'];
    const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExtension)) {
      toast({
        title: "Tipo de archivo no válido",
        description: "Solo se permiten archivos HTML (.html, .htm) o Excel (.xls, .xlsx)",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setFile(selectedFile);
    setValidationResult(null);
    setParsResult(null);
    validateFile(selectedFile);
  };

  const validateFile = async (fileToValidate) => {
    setIsValidating(true);
    
    try {
      // Determinar tipo de archivo
      const fileExtension = fileToValidate.name.toLowerCase().substring(fileToValidate.name.lastIndexOf('.'));
      const isExcelFile = ['.xls', '.xlsx'].includes(fileExtension);
      
      if (isExcelFile) {
        // Para archivos Excel, validación básica por tamaño y extensión
        if (fileToValidate.size > 10 * 1024 * 1024) { // 10MB máximo
          throw new Error('El archivo Excel es demasiado grande (máximo 10MB)');
        }
        
        setValidationResult({
          isValid: true,
          message: `Archivo Excel NAPSIS válido detectado (${(fileToValidate.size / (1024 * 1024)).toFixed(1)}MB)`
        });
        
        // Mostrar información del archivo antes del parseado
        console.log('📂 Archivo Excel NAPSIS detectado:', {
          nombre: fileToValidate.name,
          tamaño: `${(fileToValidate.size / (1024 * 1024)).toFixed(1)}MB`,
          tipo: 'Excel (.xlsx/.xls)',
          mensaje: 'Iniciando parseado automático...'
        });
        
        // Auto-parsear Excel después de validar
        await parseFileDataAuto(fileToValidate, 'excel');
        
      } else {
        // Para archivos HTML, usar validación existente
        await paymentSyncService.validateNapsisFile(fileToValidate);
        setValidationResult({
          isValid: true,
          message: "Archivo HTML NAPSIS válido detectado"
        });
        
        // Auto-parsear HTML después de validar
        await parseFileDataAuto(fileToValidate, 'html');
      }
      
      toast({
        title: "Archivo válido y procesado",
        description: `El archivo NAPSIS ${isExcelFile ? 'Excel' : 'HTML'} fue validado y parseado correctamente`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setValidationResult({
        isValid: false,
        message: error.message
      });
      
      if (onError) {
        onError(error);
      }
    } finally {
      setIsValidating(false);
    }
  };

  const parseFileDataAuto = async (fileToProcess, fileType) => {
    setIsParsing(true);
    
    try {
      let fileContent;
      let parsedData;
      
      if (fileType === 'excel') {
        // Para archivos Excel, leer como ArrayBuffer
        fileContent = await readFileContentAsArrayBuffer(fileToProcess);
        console.log('🚀 Iniciando parseado automático de Excel...');
        parsedData = paymentSyncService.parseNapsisData(fileContent, 'excel', (progress) => {
        });
        console.log('✅ Parseado Excel completado:', {
          estudiantes: parsedData.length,
          totalPagos: parsedData.reduce((sum, s) => sum + s.pagos.length, 0),
          pagosCompletados: parsedData.reduce((sum, s) => sum + s.pagos.filter(p => p.estado === 'completed').length, 0)
        });
        
        // LOG PRIMER PASO: Ver RUTs procesados del Excel
        console.log(`📊 PRIMER PASO - RUTs procesados del Excel (primeros 50):`, parsedData.slice(0, 50).map(s => `${s.rut} - ${s.nombre}`));
      } else {
        // Para archivos HTML, leer como texto
        fileContent = await readFileContent(fileToProcess);
        console.log('🚀 Iniciando parseado automático de HTML...');
        parsedData = paymentSyncService.parseNapsisData(fileContent, 'html', (progress) => {
          console.log(`Progreso HTML: ${progress}%`);
        });
      }
      
      setParsResult({
        success: true,
        data: parsedData,
        statistics: {
          totalStudents: parsedData.length,
          totalPayments: parsedData.reduce((sum, student) => sum + student.pagos.length, 0),
          completedPayments: parsedData.reduce((sum, student) => 
            sum + student.pagos.filter(pago => pago.estado === 'completed').length, 0),
          pendingPayments: parsedData.reduce((sum, student) => 
            sum + student.pagos.filter(pago => pago.estado === 'pending').length, 0)
        }
      });
      
      // NO llamar onDataImported automáticamente
      // El usuario debe revisar los logs primero y presionar "Continuar"
      console.log('✅ Parseado completado. Revisa los logs y presiona Continuar para proceder.');
      
    } catch (error) {
      console.error('Error en parseado automático:', error);
      setParsResult({
        success: false,
        error: error.message
      });
      
      if (onError) {
        onError(error);
      }
    } finally {
      setIsParsing(false);
    }
  };

  const parseFileData = async () => {
    if (!file || !validationResult?.isValid) return;
    
    setIsParsing(true);
    
    try {
      // Determinar tipo de archivo
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      const isExcelFile = ['.xls', '.xlsx'].includes(fileExtension);
      
      let fileContent;
      let parsedData;
      
      if (isExcelFile) {
        // Para archivos Excel, leer como ArrayBuffer
        fileContent = await readFileContentAsArrayBuffer(file);
        parsedData = paymentSyncService.parseNapsisData(fileContent, 'excel', (progress) => {
          // Callback de progreso para archivos grandes
          console.log(`Progreso Excel: ${progress}%`);
        });
      } else {
        // Para archivos HTML, leer como texto
        fileContent = await readFileContent(file);
        parsedData = paymentSyncService.parseNapsisData(fileContent, 'html', (progress) => {
          // Callback de progreso para archivos grandes
          console.log(`Progreso HTML: ${progress}%`);
        });
      }
      
      setParsResult({
        success: true,
        data: parsedData,
        statistics: {
          totalStudents: parsedData.length,
          totalPayments: parsedData.reduce((sum, student) => sum + student.pagos.length, 0),
          completedPayments: parsedData.reduce((sum, student) => 
            sum + student.pagos.filter(pago => pago.estado === 'completed').length, 0),
          pendingPayments: parsedData.reduce((sum, student) => 
            sum + student.pagos.filter(pago => pago.estado === 'pending').length, 0)
        }
      });
      
      if (onDataImported) {
        onDataImported(parsedData);
      }
      
      toast({
        title: "Datos procesados",
        description: `Se procesaron ${parsedData.length} estudiantes desde archivo ${isExcelFile ? 'Excel' : 'HTML'}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
    } catch (error) {
      setParsResult({
        success: false,
        error: error.message
      });
      
      if (onError) {
        onError(error);
      }
      
      toast({
        title: "Error procesando archivo",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsParsing(false);
    }
  };

  const readFileContent = (fileToRead) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      
      reader.onerror = () => {
        reject(new Error('Error leyendo archivo'));
      };
      
      reader.readAsText(fileToRead, 'UTF-8');
    });
  };

  const readFileContentAsArrayBuffer = (fileToRead) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        resolve(new Uint8Array(e.target.result));
      };
      
      reader.onerror = () => {
        reject(new Error('Error leyendo archivo Excel'));
      };
      
      reader.readAsArrayBuffer(fileToRead);
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const continueWithComparison = () => {
    if (parseResult?.success && parseResult.data && onDataImported) {
      console.log('🚀 Iniciando comparación con', parseResult.data.length, 'estudiantes...');
      onDataImported(parseResult.data);
    }
  };

  const resetImporter = () => {
    setFile(null);
    setValidationResult(null);
    setParsResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card shadow="sm">
      <CardHeader>
        <HStack spacing={3}>
          <Icon as={FiUpload} color="blue.500" boxSize={5} />
          <Text fontSize="lg" fontWeight="semibold">
            Importar Datos NAPSIS
          </Text>
        </HStack>
      </CardHeader>
      
      <CardBody>
        <VStack spacing={6} align="stretch">
          
          {/* Área de carga de archivo */}
          <Box
            border="2px dashed"
            borderColor={dragActive ? "blue.400" : borderColor}
            borderRadius="md"
            p={8}
            textAlign="center"
            bg={dragActive ? "blue.50" : hoverBg}
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ bg: hoverBg, borderColor: "blue.300" }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <VStack spacing={4}>
              <Icon as={FiFile} boxSize={12} color="gray.400" />
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium">
                  {file ? file.name : "Seleccionar archivo NAPSIS"}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Arrastra y suelta o haz clic para seleccionar
                </Text>
                <Text fontSize="xs" color="gray.400">
                  Formatos soportados: .html, .htm, .xls, .xlsx
                </Text>
              </VStack>
              
              {file && (
                <Badge colorScheme="blue" variant="subtle">
                  {file.size > 1024 * 1024 
                    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                    : `${(file.size / 1024).toFixed(1)} KB`
                  }
                </Badge>
              )}
            </VStack>
            
            <Input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm,.xls,.xlsx"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              display="none"
            />
          </Box>

          {/* Estado de validación */}
          {isValidating && (
            <Alert status="info">
              <AlertIcon />
              <VStack align="start" spacing={2} flex={1}>
                <AlertTitle>Validando archivo...</AlertTitle>
                <Progress size="sm" isIndeterminate colorScheme="blue" w="100%" />
              </VStack>
            </Alert>
          )}

          {validationResult && (
            <Alert status={validationResult.isValid ? "success" : "error"}>
              <AlertIcon />
              <VStack align="start" spacing={1} flex={1}>
                <AlertTitle>
                  {validationResult.isValid ? "Archivo válido" : "Archivo no válido"}
                </AlertTitle>
                <AlertDescription fontSize="sm">
                  {validationResult.message}
                </AlertDescription>
              </VStack>
            </Alert>
          )}

          {/* Estado de procesamiento automático */}
          {isParsing && (
            <Alert status="info">
              <AlertIcon />
              <VStack align="start" spacing={2} flex={1}>
                <AlertTitle>Procesando datos automáticamente...</AlertTitle>
                <Progress size="sm" isIndeterminate colorScheme="blue" w="100%" />
                <AlertDescription fontSize="sm">
                  Revisa la consola para ver los logs del parseado
                </AlertDescription>
              </VStack>
            </Alert>
          )}

          {/* Resultados del procesamiento */}
          {parseResult && (
            <Box>
              {parseResult.success ? (
                <Alert status="success">
                  <AlertIcon />
                  <VStack align="start" spacing={3} flex={1}>
                    <AlertTitle>Datos procesados exitosamente</AlertTitle>
                    <AlertDescription>
                      <VStack align="start" spacing={2}>
                        <List spacing={1} fontSize="sm">
                          <ListItem>
                            <ListIcon as={FiUser} color="green.500" />
                            <strong>{parseResult.statistics.totalStudents}</strong> estudiantes encontrados
                          </ListItem>
                          <ListItem>
                            <ListIcon as={FiCreditCard} color="green.500" />
                            <strong>{parseResult.statistics.totalPayments}</strong> registros de pago
                          </ListItem>
                          <ListItem>
                            <ListIcon as={FiCheck} color="green.500" />
                            <strong>{parseResult.statistics.completedPayments}</strong> pagos completados
                          </ListItem>
                          <ListItem>
                            <ListIcon as={FiX} color="orange.500" />
                            <strong>{parseResult.statistics.pendingPayments}</strong> pagos pendientes
                          </ListItem>
                        </List>
                      </VStack>
                    </AlertDescription>
                  </VStack>
                </Alert>
              ) : (
                <Alert status="error">
                  <AlertIcon />
                  <VStack align="start" spacing={1} flex={1}>
                    <AlertTitle>Error procesando datos</AlertTitle>
                    <AlertDescription fontSize="sm">
                      {parseResult.error}
                    </AlertDescription>
                  </VStack>
                </Alert>
              )}
            </Box>
          )}

          {/* Controles */}
          {(file || parseResult) && (
            <>
              <Divider />
              <HStack justify="space-between">
                <Button
                  variant="outline"
                  onClick={resetImporter}
                  leftIcon={<Icon as={FiX} />}
                >
                  Limpiar
                </Button>
                
                {parseResult?.success && (
                  <Button
                    colorScheme="blue"
                    size="lg"
                    onClick={continueWithComparison}
                    leftIcon={<Icon as={FiCheck} />}
                  >
                    Continuar con Comparación
                  </Button>
                )}
              </HStack>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default NapsisImporter;